"use client"

import { useState, useContext, useEffect, useMemo, useRef } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { formatNumberForDisplay, formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"
import {
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  FormControl,
  Select,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Alert,
  Fade,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  IconButton,
} from "@mui/material"
import CrearEntidadClienteModalComponent from "../adminComponent/CrearEntidadClienteModalComponent.js"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import dayjs from "dayjs"


import {
  ReceiptLong as ReceiptLongIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  MonetizationOn as MonetizationOnIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  LocationOn as LocationOnIcon,
  Close as CloseIcon,
} from "@mui/icons-material"

const GestionGastosSimple = ({ onSuccess, onClose, mode = "create", initialData = null, onSubmit = null, titleOverride = null }) => {
  const {
    monedas,
    getMoneda,
    tipoMedioDePago,
    getTipoMedioDePago,
    subCategoriaTransaccion,
    getSubCategoriaTransaccion,
    postTransaccion,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    postCrearPago,
    updateCuentaCorriente,
    postTransaccionPago,
    categoriaTransaccion,
    getCategoriaTransaccion,
    user,
    getSubCategoriaTransaccionByCategoria,
    getFechaHoraActualClient,
    postCuentaCorriente,
    getCuentaCorrienteByIdEntidad,
    getUbicacion,
    ubicaciones,
    getTipoEntidad,
    tipoEntidades,
    formatoNumericoGlobal,
    getParametrosGlobales,
    } = useContext(ErpContext)

  // Configuración de locale numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // Estados principales
  const [formData, setFormData] = useState({
    tipoEntidad: null,
    entidad: null,
    ubicacion: null,
    moneda: 1, // Peso argentino por defecto
    subcategoria: "",
    tipoMedioPago: null,
    medioPago: null,
    monto: "", // Valor numérico interno
    descripcion: "",
    aplicarCuentaCorriente: false,
  })

  const [montoDisplay, setMontoDisplay] = useState("") // Valor formateado para mostrar
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [tiposEntidadFiltrados, setTiposEntidadFiltrados] = useState([])
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("")
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState("Cargando...")
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [ocultarCheckboxCC, setOcultarCheckboxCC] = useState(false)
  const [tildeCheckboxCC, setTildeCheckboxCC] = useState(false)
  const mostrarUbicacion = false
  const autoProveedorGenericoAplicado = useRef(false)
  const [initialApplied, setInitialApplied] = useState(false)

  // Estados para creación/edición de entidades (proveedor)
  const [abrirModalCrearEntidad, setAbrirModalCrearEntidad] = useState(false)
  const [entidadAEditar, setEntidadAEditar] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  const today = dayjs()
  const [startDate, setStartDate] = useState(today)


  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setLoadingMessage("Cargando datos iniciales...")
    try {
      await getMoneda()
      await getTipoMedioDePago()
      await getSubCategoriaTransaccion()
      await getCategoriaTransaccion()
      await getUbicacion()
      await getTipoEntidad()

      // Cargar parámetros globales para checkbox de cuenta corriente
      const parametros = await getParametrosGlobales([
        "verCheckboxCrearGastoAfectaCuentaCorriente",
        "valorCheckboxCrearGastoAfectaCuentaCorriente",
      ])
      const ocultarCC =
        parametros?.verCheckboxCrearGastoAfectaCuentaCorriente === "0" ||
        parametros?.verCheckboxCrearGastoAfectaCuentaCorriente === 0
      setOcultarCheckboxCC(ocultarCC)

      const tildarCC =
        parametros?.valorCheckboxCrearGastoAfectaCuentaCorriente === "1" ||
        parametros?.valorCheckboxCrearGastoAfectaCuentaCorriente === 1
      setTildeCheckboxCC(tildarCC)
      if (tildarCC) {
        setFormData((prev) => ({ ...prev, aplicarCuentaCorriente: true }))
      }
    } catch (err) {
      console.error("Error al cargar datos:", err)
      setError("No se pudieron cargar los datos. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (tipoEntidades.length > 0) {
      const filtrados = tipoEntidades.filter(tipo => tipo.verEnGasto === true)
      setTiposEntidadFiltrados(filtrados)
    }
  }, [tipoEntidades])

  // Preseleccionar "Proveedor" (id = 2) por defecto
  useEffect(() => {
    if (tipoEntidades.length > 0 && !formData.tipoEntidad) {
      const proveedor = tipoEntidades.find((tipo) => tipo.id === 2)
      if (proveedor && proveedor.verEnGasto === true) {
        setFormData((prev) => ({ ...prev, tipoEntidad: proveedor }))
      }
    }
  }, [tipoEntidades, formData.tipoEntidad])

  // Preseleccionar ubicación con id = 1 por defecto
  useEffect(() => {
    if (ubicaciones.length > 0 && !formData.ubicacion) {
      const ubicacionDefault = ubicaciones.find((ubic) => ubic.id === 1)
      if (ubicacionDefault) {
        setFormData((prev) => ({ ...prev, ubicacion: ubicacionDefault }))
      }
    }
  }, [ubicaciones, formData.ubicacion])

  useEffect(() => {
    if (formData.tipoMedioPago) {
      getMedioDePagoIdTipoEspecifico(formData.tipoMedioPago)
    }
  }, [formData.tipoMedioPago])

  useEffect(() => {
    if (searchTextCliente.length >= 3 && formData.tipoEntidad) {
      getEntidadesClientesTresLetras(searchTextCliente, [formData.tipoEntidad.id], false, false)
    }
  }, [searchTextCliente, formData.tipoEntidad])

  // Buscar y autoseleccionar "Proveedor genérico" por defecto (solo si tipoEntidad=Proveedor y no hay entidad)
  useEffect(() => {
    if (autoProveedorGenericoAplicado.current) return
    if (formData.tipoEntidad?.id !== 2) return
    if (formData.entidad?.id) return
    // Dispara búsqueda mínima para encontrar genérico
    getEntidadesClientesTresLetras("gen", [2], false, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tipoEntidad?.id, formData.entidad?.id])

  useEffect(() => {
    if (autoProveedorGenericoAplicado.current) return
    if (formData.tipoEntidad?.id !== 2) return
    if (formData.entidad?.id) return
    if (!Array.isArray(entidadesClientesTresLetras) || entidadesClientesTresLetras.length === 0) return

    const proveedorGenerico =
      entidadesClientesTresLetras.find((e) => String(e?.descripcion || "").toLowerCase().includes("genérico")) ||
      entidadesClientesTresLetras.find((e) => String(e?.descripcion || "").toLowerCase().includes("generico")) ||
      entidadesClientesTresLetras.find((e) => String(e?.apellido || "").toLowerCase().includes("genérico")) ||
      entidadesClientesTresLetras.find((e) => String(e?.apellido || "").toLowerCase().includes("generico")) ||
      null

    if (!proveedorGenerico?.id) return

    const entidadOption = {
      label: `${proveedorGenerico.descripcion} - ${proveedorGenerico.apellido} - ${proveedorGenerico.dniCuitCuil}`,
      descripcion: proveedorGenerico.descripcion,
      apellido: proveedorGenerico.apellido,
      dniCuitCuil: proveedorGenerico.dniCuitCuil,
      id: proveedorGenerico.id,
    }

    setFormData((prev) => ({ ...prev, entidad: entidadOption }))
    autoProveedorGenericoAplicado.current = true
  }, [entidadesClientesTresLetras, formData.tipoEntidad?.id, formData.entidad?.id])

  // Pre-carga de datos para edición/duplicado
  useEffect(() => {
    if (!initialData || initialApplied) return

    const resolvedTipoEntidad =
      typeof initialData.tipoEntidad === "object"
        ? initialData.tipoEntidad
        : initialData.tipoEntidad
        ? { id: initialData.tipoEntidad }
        : null

    const resolvedUbicacion =
      typeof initialData.ubicacion === "object"
        ? initialData.ubicacion
        : initialData.ubicacion
        ? { id: initialData.ubicacion }
        : null

    const resolvedSubcategoria = initialData.subcategoria ?? ""
    const subCatObj = subCategoriaTransaccion.find((s) => s.id === resolvedSubcategoria)

    setFormData((prev) => ({
      ...prev,
      tipoEntidad: resolvedTipoEntidad,
      entidad: initialData.entidad || null,
      ubicacion: resolvedUbicacion,
      moneda: initialData.moneda || 1,
      subcategoria: resolvedSubcategoria,
      tipoMedioPago: initialData.tipoMedioPago || null,
      medioPago: initialData.medioPago || null,
      monto: initialData.monto ?? "",
      descripcion: initialData.descripcion || "",
      aplicarCuentaCorriente:
        initialData.aplicarCuentaCorriente !== undefined
          ? initialData.aplicarCuentaCorriente
          : tildeCheckboxCC,
    }))

    if (initialData.fecha) {
      const parsedDate = dayjs(initialData.fecha)
      if (parsedDate.isValid()) setStartDate(parsedDate)
    }

    if (typeof initialData.monto === "number" && initialData.monto >= 0) {
      setMontoDisplay(formatInputWithSeparators(initialData.monto, localeConfig.locale))
    }

    if (subCatObj) setCategoriaSeleccionada(subCatObj.descripcion || "")
    setInitialApplied(true)
  }, [initialData, initialApplied, localeConfig.locale, subCategoriaTransaccion, tildeCheckboxCC])

  useEffect(() => {
    setInitialApplied(false)
  }, [initialData, mode])

  // Sincronizar montoDisplay con formData.monto cuando cambia desde fuera
  useEffect(() => {
    if (formData.monto === "" || formData.monto == null) {
      setMontoDisplay("")
      return
    }

    const locale = localeConfig.locale
    const decimalSeparator = localeConfig.decimalSeparator

    if (typeof formData.monto === "number") {
      let formatted = formatInputWithSeparators(formData.monto, locale)
      setMontoDisplay(formatted)
    } else if (typeof formData.monto === "string") {
      const parsed = parseLocalizedNumber(formData.monto, locale)
      if (parsed !== null) {
        let formatted = formatInputWithSeparators(parsed, locale)
        if (formData.monto.endsWith(decimalSeparator) && !formatted.endsWith(decimalSeparator)) {
          formatted += decimalSeparator
        }
        setMontoDisplay(formatted)
      }
    }
  }, [formData.monto, localeConfig])

  const abrirCrearEntidad = () => {
    setEntidadAEditar(null)
    setIsEditing(false)
    setAbrirModalCrearEntidad(true)
  }

  const cerrarCrearEntidad = () => {
    setAbrirModalCrearEntidad(false)
  }

  const handleEntidadCreada = (entidad) => {
    const nuevaEntidadOption = {
      label: `${entidad.descripcion} - ${entidad.apellido} - ${entidad.dniCuitCuil}`,
      descripcion: entidad.descripcion,
      apellido: entidad.apellido,
      dniCuitCuil: entidad.dniCuitCuil,
      id: entidad.id,
      idTipoEntidad: entidad.idTipoEntidad,
    }

    setFormData((prev) => ({ ...prev, entidad: nuevaEntidadOption }))

    const tipoEntidadId = entidad.idTipoEntidad || formData.tipoEntidad?.id
    const filtros = tipoEntidadId ? [tipoEntidadId] : undefined
    getEntidadesClientesTresLetras("", filtros, false, false)
  }

  const handleAlertaEntidad = () => {
    // no-op
  }

  const handleInputChange = async (field, value) => {
    // 1) Actualizo el campo base inmediatamente
    setFormData((prev) => ({ ...prev, [field]: value }))

    if (field === "entidad" && !value) {
      autoProveedorGenericoAplicado.current = true
    }

    // 2) Si cambiaron subcategoría
    if (field === "subcategoria") {
      const subCatObj = subCategoriaTransaccion.find((s) => s.id === value)
      setCategoriaSeleccionada(subCatObj?.descripcion || "")
      // Solo auto-popular si entidad es null o vacía
      if (!formData.entidad || !formData.entidad.id) {
        const popular = await getSubCategoriaTransaccionByCategoria(value, null)
        console.log("Popular por subcategoría:", popular)
        if (!popular) return

        // arma el objeto igualito a los options:
        const entidadOption = {
          label: `${popular.entidad.descripcion} - ${popular.entidad.apellido} - ${popular.entidad.dniCuitCuil}`,
          descripcion: popular.entidad.descripcion,
          apellido: popular.entidad.apellido,
          dniCuitCuil: popular.entidad.dniCuitCuil,
          id: popular.entidad.id,
        }

        setFormData((prev) => ({
          ...prev,
          entidad: entidadOption,
          monto: popular.montoTotal,
          descripcion: popular.descripcion,
          tipoMedioPago: popular.transaccionPago[0]?.pago.medioDePago.tipoMedioDePago || null,
          medioPago: popular.transaccionPago[0]?.pago.medioDePago || null,
        }))
      } else {
        // Si entidad ya está seleccionada, buscar transacción con entidad + subcategoría
        const popular = await getSubCategoriaTransaccionByCategoria(value, formData.entidad.id)
        console.log("Popular por entidad + subcategoría:", popular)

        if (popular) {
          // Si encuentra transacción, actualizar datos
          setFormData((prev) => ({
            ...prev,
            monto: popular.montoTotal,
            descripcion: popular.descripcion,
            tipoMedioPago: popular.transaccionPago[0]?.pago.medioDePago.tipoMedioDePago || null,
            medioPago: popular.transaccionPago[0]?.pago.medioDePago || null,
          }))
        } else {
          // Si no encuentra nada, limpiar campos restantes
          setFormData((prev) => ({
            ...prev,
            monto: "",
            descripcion: "",
            tipoMedioPago: null,
            medioPago: null,
          }))
        }
      }
    }

    // 3) Si cambiaron entidad
    if (field === "entidad") {
      // Solo auto-popular si tanto entidad como subcategoría eran null/vacíos
      if ((!formData.entidad || !formData.entidad.id) && (!formData.subcategoria || formData.subcategoria === "")) {
        if (!value?.id) return

        const popular = await getSubCategoriaTransaccionByCategoria(null, value.id)
        console.log("Popular por entidad:", popular)

        if (!popular) return

        // Selecciono subcategoría y chip
        setFormData((prev) => ({ ...prev, subcategoria: popular.idCategorizacion }))
        const subCatObj = subCategoriaTransaccion.find((s) => s.id === popular.idCategorizacion)
        setCategoriaSeleccionada(subCatObj?.descripcion || "")

        // Actualizo monto, descripción y pagos
        setFormData((prev) => ({
          ...prev,
          monto: popular.montoTotal,
          descripcion: popular.descripcion,
          tipoMedioPago: popular.transaccionPago[0]?.pago.medioDePago.tipoMedioDePago || null,
          medioPago: popular.transaccionPago[0]?.pago.medioDePago || null,
        }))
      }
    }

    // 4) Si cambiaron tipo de entidad
    if (field === "tipoEntidad") {
      autoProveedorGenericoAplicado.current = value?.id === 2 ? false : true
      // Limpiar entidad seleccionada al cambiar tipo
      setFormData((prev) => ({ ...prev, entidad: null }))
      setSearchTextCliente("")
    }
  }

  const resetForm = () => {
    setFormData({
      tipoEntidad: null,
      entidad: null,
      ubicacion: null,
      moneda: 1,
      subcategoria: "",
      tipoMedioPago: null,
      medioPago: null,
      monto: "",
      descripcion: "",
      aplicarCuentaCorriente: tildeCheckboxCC,
    })
    setMontoDisplay("")
    setCategoriaSeleccionada("")
    setSearchTextCliente("")
    setError(null)
    autoProveedorGenericoAplicado.current = false
    setInitialApplied(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validaciones básicas
    if (!formData.tipoEntidad?.id || !formData.entidad?.id || !formData.ubicacion?.id || !formData.monto || !formData.descripcion || !formData.subcategoria) {
      setError("Por favor complete todos los campos obligatorios")
      return
    }

    if (!formData.tipoMedioPago || !formData.medioPago) {
      setError("Por favor seleccione un medio de pago")
      return
    }

    setLoading(true)
    const loadingText =
      mode === "edit"
        ? "Actualizando gasto..."
        : mode === "duplicate"
        ? "Duplicando gasto..."
        : "Guardando gasto..."
    setLoadingMessage(loadingText)
    try {
      let descripcionFinal = formData.descripcion

      if (onSubmit && typeof onSubmit === "function") {
        await onSubmit({ formData, startDate })
      } else {
        const {fechaServidor} = await getFechaHoraActualClient(startDate)
        
        console.log("fechaServidor", fechaServidor)

        // Crear transacción
        const transaccionResponse = await postTransaccion(
          formData.monto,
          5, // EGRESO
          formData.entidad.id,
          formData.subcategoria,
          descripcionFinal,
          user.id,
          formData.ubicacion.id, // ubicación seleccionada
          1, // negocio central
          0, // descuento
          0, // porcentaje descuento
          null,
          fechaServidor,
          formData.aplicarCuentaCorriente // afecta cuenta corriente
        )

        const idTransaccion = transaccionResponse.data.id

        // Crear pago
        const transaccionCrearPago = await postCrearPago(
          formData.moneda,
          1, // cotización
          formData.medioPago.id,
          formData.monto,
        )

        const idTransaccionCrearPago = transaccionCrearPago.data.id
        await postTransaccionPago(idTransaccion, idTransaccionCrearPago)

        // Actualizar cuenta corriente si es necesario
        if (formData.aplicarCuentaCorriente) {
        // Manejar cuenta corriente si es necesario
        const cuentas = await getCuentaCorrienteByIdEntidad(formData.entidad.id);
        const cuentaCorrienteConId = cuentas.find(
          (ct) => ct.idEntidad === formData.entidad.id
        );
        if (cuentaCorrienteConId) {
          await updateCuentaCorriente(formData.entidad.id, formData.monto, "compra");
        } else {
          await postCuentaCorriente(formData.entidad.id, formData.monto, "compra");
        }
        
        }

        // Resetear formulario solo cuando se crea/duplica
        resetForm()
        setStartDate(today)
      }

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      
      // Llamar callback si existe
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error("Error al guardar transacción:", error)
      setError(
        mode === "edit"
          ? "No se pudo actualizar la transacción. Intente nuevamente."
          : "No se pudo guardar la transacción. Intente nuevamente."
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: 2, maxWidth: "100%", mx: "auto", position: "relative" }}>
      {/* Botón X para cerrar */}
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "#5b5b5f",
            "&:hover": { backgroundColor: "rgba(91, 91, 95, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      {/* Encabezado simplificado */}
      <Box sx={{ mb: 2, textAlign: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
          <ReceiptLongIcon sx={{ fontSize: 28, color: "rgb(244, 147, 14)", mr: 1 }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: "#2c3e50" }}>
            {titleOverride || (mode === "edit" ? "Editar Gasto" : mode === "duplicate" ? "Duplicar Gasto" : "Nuevo Gasto")}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {mode === "edit"
            ? "Actualice los datos del gasto"
            : mode === "duplicate"
            ? "Confirme los datos para duplicar el gasto"
            : "Complete los datos para registrar un nuevo gasto"}
        </Typography>
      </Box>

      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Tipo de Entidad - Más pequeño */}
              <Grid item xs={12} md={2}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <CategoryIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Tipo de Entidad
                </Typography>
                <Autocomplete
                  size="small"
                  options={tiposEntidadFiltrados}
                  getOptionLabel={(option) => option.descripcion || ""}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  value={formData.tipoEntidad}
                  onChange={(e, newValue) => handleInputChange("tipoEntidad", newValue)}
                  sx={{ width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar tipo..."
                      required
                      fullWidth
                      size="medium"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <CategoryIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Entidad - Más grande con botón Crear Proveedor */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <PersonIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Entidad
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Autocomplete
                    size="small"
                    options={entidadesClientesTresLetras.map((it) => ({
                      label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                      descripcion: it.descripcion,
                      apellido: it.apellido,
                      dniCuitCuil: it.dniCuitCuil,
                      id: it.id,
                    }))}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={formData.entidad}
                    onChange={(e, newValue) => handleInputChange("entidad", newValue)}
                    onInputChange={(e, newInputValue) => setSearchTextCliente(newInputValue)}
                    disabled={!formData.tipoEntidad}
                    sx={{ flex: 1, width: "100%" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={formData.tipoEntidad ? "Buscar Entidad..." : "Primero seleccione tipo"}
                        required
                        fullWidth
                        size="medium"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PersonIcon color="action" fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={abrirCrearEntidad}
                    disabled={!formData.tipoEntidad}
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: 150,
                      px: 1,
                      py: 1,
                      borderColor: "#5b5b5f",
                      color: "#5b5b5f",
                      "&:hover": {
                        borderColor: "#4a4a4e",
                        backgroundColor: "rgba(91, 91, 95, 0.04)",
                      },
                    }}
                  >
                    Crear {formData.tipoEntidad?.descripcion || "Entidad"}
                  </Button>
                  {!ocultarCheckboxCC && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.aplicarCuentaCorriente}
                        onChange={(e) => handleInputChange("aplicarCuentaCorriente", e.target.checked)}
                      />
                    }
                    label="Afecta cuenta corriente"
                    // sx={{ ml: 1 }}
                  />
                  )}
                </Box>
              </Grid>

              {/* Fecha - Esquina superior derecha */}
              <Grid item xs={12} md={3}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      format="DD/MM/YYYY"
                      label="Fecha"
                      slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                      sx={{ width: "200px" }}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>

              {mostrarUbicacion && (
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                    <LocationOnIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                    Ubicación
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={ubicaciones}
                    getOptionLabel={(option) => option.descripcion || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={formData.ubicacion}
                    onChange={(e, newValue) => handleInputChange("ubicacion", newValue)}
                    sx={{ width: "70%" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Seleccionar ubicación..."
                        required
                        fullWidth
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LocationOnIcon color="action" fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Categoría y Monto */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <CategoryIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  SubCategoría
                </Typography>
                <FormControl fullWidth required size="small">
                  <Select
                    value={formData.subcategoria}
                    onChange={(e) => handleInputChange("subcategoria", e.target.value)}
                    displayEmpty
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <CategoryIcon color="action" fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="" disabled>
                      Seleccionar SubCategoría
                    </MenuItem>
                    {subCategoriaTransaccion.map((subCat) => (
                      <MenuItem key={subCat.id} value={subCat.id}>
                        {subCat.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {categoriaSeleccionada && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip size="small" label={`Tipo: ${categoriaSeleccionada}`} color="primary" variant="outlined" />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <MonetizationOnIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Monto
                </Typography>
                <TextField
                  type="text"
                  placeholder="0.00"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={montoDisplay}
                  onChange={(e) => {
                    const inputValue = e.target.value
                    const { locale, decimalSeparator } = localeConfig

                    if (inputValue === "") {
                      setMontoDisplay("")
                      setFormData((prev) => ({ ...prev, monto: "" }))
                      return
                    }

                    const parsedValue = parseLocalizedNumber(inputValue, locale)
                    if (parsedValue === null && inputValue.trim() !== "") return

                    let formattedValue =
                      parsedValue !== null ? formatInputWithSeparators(parsedValue, locale) : ""

                    const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                    const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                    const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                    if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                      formattedValue += decimalSeparator
                    } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                      const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                      const integerPart = formattedValue.split(decimalSeparator)[0]
                      formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                    }

                    setMontoDisplay(formattedValue)
                    setFormData((prev) => ({ ...prev, monto: parsedValue ?? "" }))

                    requestAnimationFrame(() => {
                      const input = e.target
                      if (input) input.setSelectionRange(formattedValue.length, formattedValue.length)
                    })
                  }}
                  onBlur={(e) => {
                    const { locale } = localeConfig
                    const parsed = parseLocalizedNumber(e.target.value, locale)
                    if (parsed !== null && parsed > 0) {
                      const formatted = formatInputWithSeparators(parsed, locale)
                      setMontoDisplay(formatted)
                      setFormData((prev) => ({ ...prev, monto: parsed }))
                    } else if (e.target.value === "") {
                      setMontoDisplay("")
                      setFormData((prev) => ({ ...prev, monto: "" }))
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MonetizationOnIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                />
              </Grid>

              {/* Medios de Pago */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <PaymentIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Tipo de Pago
                </Typography>
                <Autocomplete
                  size="small"
                  options={tipoMedioDePago}
                  getOptionLabel={(option) => option.descripcion}
                  value={formData.tipoMedioPago}
                  onChange={(event, newValue) => {
                    handleInputChange("tipoMedioPago", newValue)
                    handleInputChange("medioPago", null)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar tipo de pago..."
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <PaymentIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <PaymentIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Medio de Pago
                </Typography>
                <Autocomplete
                  size="small"
                  options={medioDePagoIdTipoEspecifico}
                  getOptionLabel={(option) => option.descripcion}
                  value={formData.medioPago}
                  onChange={(event, newValue) => handleInputChange("medioPago", newValue)}
                  disabled={!formData.tipoMedioPago}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Seleccionar medio de pago..."
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <PaymentIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Descripción */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  <DescriptionIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 20 }} />
                  Descripción
                </Typography>
                <TextField
                  placeholder="Describe el gasto..."
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  multiline
                  rows={2}
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange("descripcion", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                        <DescriptionIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Botones de acción */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      py: 1,
                      px: 4,
                      fontSize: "1rem",
                      fontWeight: 600,
                      borderColor: "#5b5b5f",
                      color: "#5b5b5f",
                      "&:hover": {
                        borderColor: "#4a4a4e",
                        backgroundColor: "rgba(91, 91, 95, 0.04)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    startIcon={<SaveIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      py: 1,
                      px: 4,
                      fontSize: "1rem",
                      fontWeight: 600,
                      backgroundColor: "#5b5b5f",
                      "&:hover": {
                        backgroundColor: "#4a4a4e",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading
                      ? loadingMessage
                      : mode === "edit"
                      ? "Guardar cambios"
                      : mode === "duplicate"
                      ? "Crear duplicado"
                      : "Guardar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Alertas */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setSuccess(false)}
        >
          {mode === "edit"
            ? "¡Gasto actualizado correctamente!"
            : mode === "duplicate"
            ? "¡Gasto duplicado correctamente!"
            : "¡Gasto registrado correctamente!"}
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">{loadingMessage}</Typography>
          </Box>
        </Box>
      )}

      {/* Modal para crear/editar entidad (Proveedor) */}
      <CrearEntidadClienteModalComponent
        open={abrirModalCrearEntidad}
        handleClose={cerrarCrearEntidad}
        entidad={entidadAEditar}
        isEditing={isEditing}
        handleAlerta={handleAlertaEntidad}
        onClienteCreado={handleEntidadCreada}
        tipoEntidad={formData.tipoEntidad?.id === 1 ? "cliente" : "proveedor"}
      />
    </Box>
  )
}

export default GestionGastosSimple
